/* eslint-disable camelcase */
import { createSlice } from '@reduxjs/toolkit';
// utils
//
import { dispatch } from '../store';
import conObj from '../../utils/connection-assistant';
import { getFingerprint, getLocalId } from '../../utils/formatObject';

// ----------------------------------------------------------------------

const initialState = {
  bank: null,
  banner: null,
  brand: null,
  contactUs: null,
  language: null,
  promotion: null,
  provider: null,
  memberInfo: null,
  userBank: null,
  userGameAccount: null,
  securityConfig: null,
  //
  inbox: [],
  unreadInboxCount: 0,
  memberBalance: {
    currencyCode: '',
    amount: 0,
    balance: 0,
    displayBalance: 0,
  },
  gameCategsAndCompanies: [],
  gameCategs: [],
  siteSettings: JSON.parse(localStorage.getItem(`site_configs`) || '{}') || {},
  promotions: { data: [], categories: [] },
  angPauPromos: { data: [], isFetchedOnce: false },
  withdrawRequirement: {
    calculationType: '',
    current: 0,
    requirement: 0,
    isSufficient: false,
    allowWithdraw: false,
  },
  customPages: [],
  favoriteGames: [],
  dailyCheckIn: {
    dailyCheckInStatus: true,
  },
  currencies: [],
};

const slice = createSlice({
  name: 'lookup',
  initialState,
  reducers: {
    setInitData(state, action) {
      const { bank, banner, brand, contactUs, language, promotion, provider } = action.payload;
      state.bank = bank;
      state.banner = banner;
      state.brand = brand;
      state.contactUs = contactUs;
      state.language = language;
      state.promotion = promotion;
      state.provider = provider;
    },
    setMemberBalance(state, action) {
      state.memberBalance = action.payload;
    },
    setInbox(state, action) {
      state.inbox = action.payload;
    },
    setUnreadInboxCount(state, action) {
      state.unreadInboxCount = action.payload;
    },
    setGameCategsAndCompanies(state, action) {
      state.gameCategsAndCompanies = action.payload;
    },
    setGameCategs(state, action) {
      state.gameCategs = action.payload;
    },
    setSiteSettings(state, action) {
      state.siteSettings = action.payload;
    },
    setFavoriteGames(state, action) {
      state.favoriteGames = action.payload;
    },
    setPromotions(state, action) {
      state.promotions = action.payload;
    },
    claimPromotion(state, action) {
      const newPromotions = JSON.parse(JSON.stringify(state.promotions?.data));

      if (newPromotions?.length) {
        state.promotions = {
          ...state.promotions,
          data: newPromotions.map((x) => {
            if (action.payload === x?.id) {
              return { ...x, is_claimable_now: false };
            }

            return x;
          }),
        };
      }
    },
    claimPromotionBanner(state, action) {
      const newBanner = JSON.parse(JSON.stringify(state.siteSettings?.sliders));

      if (newBanner?.length) {
        state.siteSettings = {
          ...state.siteSettings,
          sliders: newBanner.map((x) => {
            if (action.payload === x?.BannerSlide?.id) {
              return {
                BannerSlide: {
                  ...x?.BannerSlide,
                  is_claimable_now: false,
                },
              };
            }

            return x;
          }),
        };
      }
    },
    setAngPauPromos(state, action) {
      state.angPauPromos = action.payload;
    },
    setWithdrawRequirement(state, action) {
      state.withdrawRequirement = action.payload;
    },
    setCustomPages(state, action) {
      state.customPages = action.payload;
    },
    setMemberInfo(state, action) {
      const { memberInfo, userBank, userGameAccount, securityConfig, memberBalance } = action.payload;
      state.memberInfo = memberInfo;
      state.userBank = userBank;
      state.userGameAccount = userGameAccount;
      state.securityConfig = securityConfig;
      state.memberBalance = memberBalance;
    },
    setDailyCheckin(state, action) {
      state.dailyCheckIn = action.payload;
    },
    setCurrencies(state, action) {
      state.currencies = action.payload;
    },
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  setInitData,
  setMemberBalance,
  setInbox,
  setUnreadInboxCount,
  setGameCategsAndCompanies,
  setGameCategs,
  setSiteSettings,
  setFavoriteGames,
  setPromotions,
  claimPromotion,
  claimPromotionBanner,
  setAngPauPromos,
  setWithdrawRequirement,
  setCustomPages,
  setMemberInfo,
  setDailyCheckin,
  setCurrencies,
} = slice.actions;

export async function getUserMemberBalance() {
  const { fpResult, visitorId } = await getFingerprint();
  const localId = await getLocalId();

  const response = await conObj.get('v1/user-profile', {
    fingerprint: visitorId,
    fingerprint_data: fpResult,
    local_id: localId,
  });

  const {
    username,
    full_name: fullName,
    dob,
    membership_level: membershipLevel,
    reference_link: referenceLink,
    currency,
    reward_point: rewardPoint,
    securityConfig,
    uuid,
    user_bank: userBank,
    user_game_account: userGameAccount,
    wallet_balance: walletBalance,
    min_deposit: minDeposit,
    min_withdraw: minWithdraw,
    favourite_game: favouriteGame,
    phone_number: phoneNumber,
    total_reward_point_earn: totalRewardPointEarn,
    daily_check_in_status: dailyCheckInStatus,
    check_in_transaction: checkInTransaction,
    check_in_streak: checkInStreak,
  } = response.data?.data[0];

  const memberInfo = {
    username,
    fullName,
    dob,
    membershipLevel,
    referenceLink,
    currency,
    rewardPoint,
    uuid,
    userBank,
    userGameAccount,
    walletBalance,
    minDeposit,
    minWithdraw,
    favouriteGame,
    phoneNumber,
    checkInStreak,
  };

  dispatch(
    setMemberInfo({
      memberInfo,
      userBank,
      userGameAccount,
      favouriteGame,
      securityConfig,
      memberBalance: { currencyCode: currency, balance: walletBalance, displayBalance: walletBalance, rewardPoint },
    })
  );

  dispatch(
    setDailyCheckin({
      totalRewardPointEarn,
      dailyCheckInStatus,
      checkInTransaction,
      checkInStreak,
    })
  );

  return response.data;
}

export function getInbox(page = 0, limit = 20) {
  return conObj.post(`notifications.json?page=${page}&limit=${limit}`).then((response) => {
    dispatch(setInbox(response.data.data.data));
  });
}

export function getUnreadInboxCount(page = 0, limit = 20) {
  return conObj.post(`unread_notifications.json?page=${page}&limit=${limit}`).then((response) => {
    dispatch(setUnreadInboxCount(response.data.data.data.count));
  });
}

export function getGameCategsAndCompanies() {
  // return conObj.post('games/game_categories.json').then((response) => {
  //   dispatch(setGameCategsAndCompanies(response?.data?.data?.data));
  // });
}

export function getGameCategs() {
  return conObj.post('games/categories.json').then((response) => {
    dispatch(setGameCategs(response?.data?.data?.data));
  });
}

export async function getCurrencies() {
  try {
    const response = await conObj.get('v1/currency');
    if (response.data?.success) {
      const currencies = response.data.data;
      dispatch(setCurrencies(currencies));
      return currencies;
    }

    return [];
  } catch (error) {
    console.error('Failed to fetch currencies:', error);
    return [];
  }
}

export function getSiteSettings() {
  return conObj.get(`v1/init?currency_id=${process.env.REACT_APP_CURRENCY_ID}`).then((response) => {
    const { bank, banner, brand, contact_us: contactUs, language, promotion, provider } = response?.data?.data;

    const categoryOrder = {
      slot: { label: 'Slot', order: 1 },
      mobile_slot: { label: 'Mobile Slot', order: 2 }, // merge with slot
      live_casino: { label: 'Live Casino', order: 3 },
      poker: { label: 'Poker', order: 4 },
      sport: { label: 'Sports', order: 5 },
      esports: { label: 'E-Sports', order: 6 },
      lottery: { label: 'Lottery', order: 7 },
    };

    const providerArray = Object.entries(provider || {})
      .filter(([key]) => categoryOrder[key]) // only include known categories
      .map(([key, value]) => ({
        name: categoryOrder[key].label,
        slug: key,
        order: categoryOrder[key].order,
        companies: value.map((company) => ({
          ...company,
          is_parent: true,
        })),
      }))
      .sort((a, b) => a.order - b.order);

    const currentLang = localStorage.getItem('i18nextLng');
    const groupedBanners = banner.filter((f) => f?.language?.toLowerCase() === currentLang);

    const _siteSettings = {
      // announcement_text: response.data?.data?.data?.announcement_text,
      // sliders: response.data?.data?.data?.sliders,
      languages: language,
      memberGroupBanners: response.data?.data?.data?.member_group_banners,
      referralBanners: response.data?.data?.data?.referral_banners,
      // popups: _popupBanner,
      banners: groupedBanners,
    };
    dispatch(setSiteSettings(_siteSettings));
    dispatch(setGameCategsAndCompanies(providerArray));
    dispatch(setPromotions({ data: promotion, catagories: [] }));

    localStorage.setItem(`site_settings`, JSON.stringify(_siteSettings));

    dispatch(
      setInitData({
        bank,
        banner: groupedBanners,
        brand,
        contactUs,
        language,
        provider,
      })
    );
  });
}

export function getPromotions() {
  // return conObj.post('promotion.json').then((response) => {
  //   // dispatch(setPromotions({ data: response.data?.data?.data, categories: response.data?.data?.categories }));
  // });
}

export function getAngPauPromotion() {
  return conObj
    .post('promotion.json', {
      promotion_type: 'angpau_manual',
    })
    .then((response) => {
      const respData = response.data?.data?.data;
      if (respData) dispatch(setAngPauPromos({ data: respData, isFetchedOnce: true }));
    });
}

export function getPages() {
  // return conObj.post(`/pages.json`).then((res) => {
  //   if (res.data?.data?.data?.length > 0) {
  //     dispatch(setCustomPages(res.data?.data?.data?.map((item) => ({ pageKey: item?.page_key, title: item?.title }))));
  //   }
  // });
}
